import React, { useEffect, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { add } from "date-fns";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { MaterialRequestReceiptPDF } from "./PurchaseOrderReceipt";

export default function MaterialRequest({ onClose, Request, onApprove, onReject, onProceedToRFQ }) {
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  );
  const [selectedItems, setSelectedItems] = useState([]);
  const { addAlert } = useAlert();
  const [warehouseMaterials, setWarehouseMaterials] = useState([]);
  const [showRejectComment, setShowRejectComment] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [editComment, setEditComment] = useState("");

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getMaterials = async () => {
    try {
      const res = await fetch(`${BASE_URL}/org/warehouse/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }

      const data = await res.json();
      console.log("Fetched warehouse materials:", data);
      setWarehouseMaterials(data);
    } catch (err) {
      // setError("Failed to load materials from library");
      console.error("Error fetching materials:", err);
      addAlert("Failed to load materials from library", "error");
    }
  };

  useEffect(() => {
    getMaterials();
  }, [organisationId]);

  const handleSendFromWarehouse = async () => {
    try {
      const requestBody = {
        lines: selectedItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
      };
      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${Request.id}/send-from-warehouse/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        addAlert(
          errorData.error || "Failed to send material request from warehouse",
          "error"
        );
        // console.error("Error details:", errorData);
        throw new Error("Failed to send material request from warehouse");
      }
      const data = await response.json();
      console.log("Material request sent from warehouse:", data);
      addAlert("Material request sent from warehouse successfully", "success");
      onClose();
      // Optionally, you can refresh the material request details here
    } catch (err) {
      console.error("Error sending material request from warehouse:", err);
      // addAlert("Failed to send material request from warehouse", "error");
    }
  };

  const handleProceedToRFQ = async () => {
    try {
      const requestBody = {
        organization: organisationId,
        material_request: Request.id,
        sites: Request.sites,
        lines: selectedItems.map((item) => ({
          material: item.material,
          quantity: item.quantity,
          mr_lines: item.id,
        })),
      };
      const response = await fetch(`${BASE_URL}/inventory/rfq/create/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Failed to create RFQ from material request");
      }
      const data = await response.json();
      console.log("RFQ created from material request:", data);
      addAlert("RFQ created from material request successfully", "success");
      onClose();
      // after 1 sec
      setTimeout(() => {
        onProceedToRFQ();
      }, 1000);
      // Optionally, you can refresh the material request details here
    } catch (err) {
      console.error("Error creating RFQ from material request:", err);
      addAlert("Failed to create RFQ from material request", "error");
    }
  };

  const handleApproveReject = async (action) => {
    if (action === "rejected" && !showRejectComment) {
      setShowRejectComment(true);
      return;
    }

    try {
      const requestBody = { action };
      if (action === "rejected" && rejectComment.trim()) {
        requestBody.comment = rejectComment.trim();
      }

      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${Request.id}/approve-reject/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} material request`);
      }
      const data = await response.json();
      console.log(`Material request ${action}:`, data);
      addAlert(`Material request ${action} successfully`, "success");
      onClose();
      setTimeout(() => {
        if (action === "approved") {
          onApprove();
        } else {
          onReject();
        }
      }, 1000);
      // Optionally, you can refresh the material request details here
    } catch (err) {
      console.error(`Error trying to ${action} material request:`, err);
      addAlert(`Failed to ${action} material request`, "error");
    }
  };

  const handleUpdate = async () => {
    try {
      const requestBody = {
        comment: editComment,
        sites: Request.sites,
        lines: editLines.map((line) => ({
          id: line.id,
          material: line.material,
          quantity: parseFloat(line.quantity),
        })),
      };

      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${Request.id}/update/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update material request");
      }

      const data = await response.json();
      console.log("Material request updated:", data);
      addAlert("Material request updated successfully", "success");
      setIsEditing(false);
      onClose(); // Close modal to refresh data in parent
      if (onApprove) onApprove(); // Trigger refresh in parent
    } catch (err) {
      console.error("Error updating material request:", err);
      addAlert(err.message || "Failed to update material request", "error");
    }
  };

  const startEditing = () => {
    setEditLines(
      Request.material_request_lines.map((line) => ({
        id: line.id,
        material: line.material,
        quantity: line.quantity,
      }))
    );
    setEditComment("");
    setIsEditing(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative flex flex-row items-center px-6 py-3 border-b border-[#C3C3C3]">
          <h2 className="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-gray-900">
            Material Request
          </h2>
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Site Name + Download button on same row */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-purple-900 bg-purple-100 px-3 py-1 rounded-full">
              {Request.site_name}
            </span>
            <PDFDownloadLink
              document={
                <MaterialRequestReceiptPDF
                  request={Request}
                  warehouseMaterials={warehouseMaterials}
                />
              }
              fileName={`Material_Request_${Request?.mr_number || Request?.code || "receipt"
                }.pdf`}
              className="px-3 py-1.5 text-xs sm:text-sm bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 transition-colors"
            >
              {({ loading }) =>
                loading ? "Generating receipt..." : "Download Request"
              }
            </PDFDownloadLink>
          </div>
          {/* Request Info */}
          <div className="rounded-xl p-4 mb-6 border-[1px] border-[#C3C3C3]">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-base font-semibold text-[#232323]">
                  {Request.mr_number}
                </div>
              </div>
              <div className="text-left">
                <div className="text-sm text-[#737373]">Raised by</div>
                <div className="text-sm font-medium text-[#232323]">
                  {Request.raisedBy}
                </div>
              </div>
              <div className="text-left">
                <div className="text-sm text-[#737373] mb-1">
                  Delivery expected by
                </div>
                <div className="text-sm font-medium text-[#232323]">
                  {formatDate(Request.expectedBy)}
                </div>
              </div>
            </div>
          </div>

          {/* Content Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900">Content</h3>
            <span className="text-sm text-gray-500">
              Total {Request.items} items requested
            </span>
          </div>

          {/* select all checkbox */}
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300"
              color="#3B0069"
              style={{ accentColor: "#3B0069" }}
              checked={
                selectedItems.length ===
                Request.material_request_lines.filter(
                  (item) => item.status === "DRAFT"
                ).length && selectedItems.length > 0
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedItems(
                    Request.material_request_lines.filter(
                      (item) => item.status === "DRAFT"
                    )
                  );
                } else {
                  setSelectedItems([]);
                }
              }}
              disabled={
                Request.status === "COMPLETED" ||
                Request.status === "PENDING_APPROVAL" ||
                Request.status === "REJECTED"
              }
            />
            <span className="text-sm text-[#737373] font-light ml-2">
              Select All
            </span>
          </div>

          {/* Material Items */}
          <div className="space-y-3">
            {Request.material_request_lines.map((item, index) => {
              const isClickable =
                item.status === "DRAFT" &&
                Request.status !== "COMPLETED" &&
                Request.status !== "REJECTED" &&
                Request.status !== "PENDING_APPROVAL";
              const statusConfig = {
                COMPLETED: {
                  color: "bg-green-100 text-green-700",
                  label: "Completed",
                },
                SENT: { color: "bg-yellow-100 text-yellow-700", label: "Sent" },
                CANCELLED: {
                  color: "bg-red-100 text-red-700",
                  label: "Cancelled",
                },
                DRAFT: { color: "", label: "" },
              };

              return (
                <div
                  key={item.id}
                  className={`px-2 py-2 rounded-lg ${!isClickable && !isEditing
                    ? "opacity-60" : ""
                    }`}
                  style={{
                    boxShadow:
                      " 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {/* Item Header */}
                  <div className="relative flex items-center justify-start gap-3 py-1 px-2 border-b-[0.5px] border-[#C3C3C3]">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300"
                      color="#3B0069"
                      style={{ accentColor: "#3B0069" }}
                      disabled={!isClickable}
                      checked={
                        selectedItems.find((i) => i.id === item.id) !==
                        undefined
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems((prev) => [...prev, item]);
                        } else {
                          setSelectedItems((prev) =>
                            prev.filter((i) => i.id !== item.id)
                          );
                        }
                      }}
                    />
                    <span className="text-sm text-[#737373] font-light">
                      Material {index + 1}
                    </span>
                    {/* Centered label above the warehouse quantity column */}
                    <span className="absolute left-[41.5%] transform -translate-x-1/2 text-sm text-[#737373] font-light">
                      Warehouse
                    </span>
                    {item.status !== "DRAFT" && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig[item.status]?.color
                          }`}
                      >
                        {statusConfig[item.status]?.label}
                      </span>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex flex-row items-center justify-between py-1 px-10">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-sm font-medium text-[#232323]">
                        {item?.material_name}
                      </span>
                      <span className="text-sm font-normal text-[#737373]">
                        {item?.subcategory}
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-sm font-medium text-[#232323]">
                        {warehouseMaterials.find(
                          (m) => m.material.id === item.material
                        )?.current_quantity || 0}{" "}
                        {item?.uom}
                      </span>
                      <span className="text-sm font-normal text-[#737373]">
                        In stock
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={
                              editLines.find((l) => l.id === item.id)
                                ?.quantity || ""
                            }
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setEditLines((prev) =>
                                prev.map((l) =>
                                  l.id === item.id
                                    ? { ...l, quantity: newVal }
                                    : l
                                )
                              );
                            }}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-[#232323]">
                            {item?.uom}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-[#232323]">
                          {item?.quantity} {item?.uom}
                        </span>
                      )}
                      <span className="text-sm font-normal text-[#737373]">
                        Requested quantity
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="border-t border-gray-200 px-6 py-4 flex gap-3"
          style={{
            display:
              Request.status === "COMPLETED" ||
                Request.status === "PENDING_APPROVAL" ||
                Request.status === "REJECTED"
                ? "none"
                : "flex",
          }}
        >
          <button
            onClick={handleSendFromWarehouse}
            style={{
              cursor:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "not-allowed"
                  : "pointer",
              backgroundColor:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "#E0E0E0"
                  : "#3B0069",
              color:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "#A3A3A3"
                  : "#FFFFFF",
            }}
            className="flex-1 bg-purple-900 hover:bg-purple-800 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            disabled={
              Request.status === "COMPLETED" || selectedItems.length === 0
            }
          >
            Send from warehouse
          </button>
          <button
            onClick={handleProceedToRFQ}
            style={{
              cursor:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "not-allowed"
                  : "pointer",
              backgroundColor:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "#E0E0E0"
                  : "#F3E8FF",
              color:
                Request.status === "COMPLETED" || selectedItems.length === 0
                  ? "#A3A3A3"
                  : "#3B0069",
            }}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
            disabled={
              Request.status === "COMPLETED" || selectedItems.length === 0
            }
          >
            Proceed to RFQ
          </button>
        </div>

        {/* Approval/Rejection Section */}
        <div
          className="border-t border-gray-200 px-6 py-4"
          style={{
            display: Request.status === "PENDING_APPROVAL" ? "block" : "none",
          }}
        >
          {/* Rejection/Edit Comment Field */}
          {(showRejectComment || isEditing) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isEditing ? "Update Comment" : "Reason for rejection (optional)"}
              </label>
              <textarea
                value={isEditing ? editComment : rejectComment}
                onChange={(e) =>
                  isEditing
                    ? setEditComment(e.target.value)
                    : setRejectComment(e.target.value)
                }
                placeholder={isEditing ? "Add a comment for this update..." : "Enter reason for rejection..."}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="flex-1 bg-purple-900 hover:bg-purple-800 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (showRejectComment) {
                      setShowRejectComment(false);
                      setRejectComment("");
                    } else {
                      handleApproveReject("rejected");
                    }
                  }}
                  className="flex-1 bg-gray-100 hover:bg-red-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {showRejectComment ? "Cancel" : "Reject"}
                </button>
                {showRejectComment && (
                  <button
                    onClick={() => handleApproveReject("rejected")}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                  >
                    Confirm Rejection
                  </button>
                )}
                {!showRejectComment && (
                  <>
                    <button
                      onClick={startEditing}
                      className="flex-1 bg-gray-100 hover:bg-purple-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      Edit Request
                    </button>
                    <button
                      onClick={() => handleApproveReject("approved")}
                      className="flex-1 bg-gray-100 hover:bg-green-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      Approve
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
